// AuthLogs.js - Interface de Logs de Autenticação Smart Home v4.1
// Apenas para administradores
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Shield, RefreshCw, User, Clock, AlertTriangle,
  CheckCircle, XCircle, LogOut, Filter, Trash2, Activity
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './AuthLogs.css';

const EVENT_TYPES = {
  login: { icon: CheckCircle, color: '#22c55e' },
  logout: { icon: LogOut, color: '#3b82f6' },
  login_failed: { icon: XCircle, color: '#ef4444' },
  token_refresh: { icon: RefreshCw, color: '#f59e0b' }
};

function AuthLogs({ apiUrl, onBack, token }) {
  const { t, i18n } = useTranslation();
  const loc = () => (i18n.language === 'en' ? 'en-GB' : 'pt-PT');
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState({
    username: '',
    eventType: '',
    days: 7
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadLogs();
    loadSummary();
  }, [filter]);

  const authHeaders = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const loadLogs = async () => {
    try {
      setError('');
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (filter.username) params.append('username', filter.username);
      if (filter.eventType) params.append('event_type', filter.eventType);
      if (filter.days) params.append('days', filter.days);

      const response = await fetch(`${apiUrl}/auth/logs?${params}`, {
        headers: authHeaders
      });

      if (response.status === 403) {
        setError(t('autenticacao.acessoNegado'));
        return;
      }

      const data = await response.json();
      setLogs(data.logs || []);
    } catch (err) {
      console.error('Erro ao carregar logs:', err);
      setError(t('autenticacao.erroCarregar'));
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await fetch(`${apiUrl}/auth/logs/summary?days=${filter.days}`, {
        headers: authHeaders
      });

      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Erro ao carregar resumo:', err);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm(t('autenticacao.confirmarLimpeza'))) return;

    try {
      const response = await fetch(`${apiUrl}/auth/logs/cleanup?days=90`, {
        method: 'DELETE',
        headers: authHeaders
      });

      const data = await response.json();
      if (data.success) {
        alert(`${data.deleted_count} logs eliminados`);
        loadLogs();
        loadSummary();
      }
    } catch (err) {
      setError(t('autenticacao.erroLimpar'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString(loc());
  };

  const formatUserAgent = (ua) => {
    if (!ua) return '-';
    // Simplificar user agent
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return ua.substring(0, 30) + '...';
  };

  const EventIcon = ({ type }) => {
    const config = EVENT_TYPES[type] || EVENT_TYPES.login;
    const Icon = config.icon;
    return <Icon size={18} style={{ color: config.color }} />;
  };

  if (loading) {
    return (
      <div className="auth-logs-container">
        <div className="auth-logs-loading">
          <Shield size={48} className="spin" />
          <p>{t('autenticacao.aCarregar')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-logs-container">
      <div className="auth-logs-content">
        {/* Header */}
        <div className="auth-logs-header">
          <button onClick={onBack} className="btn-back">
            <ArrowLeft size={24} />
          </button>
          <div className="header-info">
            <div className="header-title">
              <Shield size={32} />
              <h1>{t('autenticacao.titulo')}</h1>
            </div>
            <span className="header-subtitle">
              {logs.length} eventos nos últimos {filter.days} dias
            </span>
          </div>
          <div className="header-actions">
            <button 
              onClick={() => setShowFilters(!showFilters)} 
              className={`btn-filter ${showFilters ? 'active' : ''}`}
            >
              <Filter size={20} />
            </button>
            <button onClick={handleCleanup} className="btn-cleanup" title={t('autenticacao.limparAntigos')}>
              <Trash2 size={20} />
            </button>
            <button onClick={() => { loadLogs(); loadSummary(); }} className="btn-refresh">
              <RefreshCw size={20} />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="error-banner">
            <AlertTriangle size={20} />
            {error}
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="summary-grid">
            <div className="summary-card">
              <Activity size={24} />
              <div className="summary-info">
                <span className="summary-value">{summary.total_events}</span>
                <span className="summary-label">{t('autenticacao.totalEventos')}</span>
              </div>
            </div>
            <div className="summary-card success">
              <CheckCircle size={24} />
              <div className="summary-info">
                <span className="summary-value">{summary.events_by_type?.login || 0}</span>
                <span className="summary-label">{t('autenticacao.logins')}</span>
              </div>
            </div>
            <div className="summary-card danger">
              <XCircle size={24} />
              <div className="summary-info">
                <span className="summary-value">{summary.failed_logins || 0}</span>
                <span className="summary-label">{t('autenticacao.falhados')}</span>
              </div>
            </div>
            <div className="summary-card info">
              <LogOut size={24} />
              <div className="summary-info">
                <span className="summary-value">{summary.events_by_type?.logout || 0}</span>
                <span className="summary-label">{t('autenticacao.logouts')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        {showFilters && (
          <div className="filters-panel">
            <div className="filter-group">
              <label>{t('autenticacao.filtroUtilizador')}</label>
              <input
                type="text"
                value={filter.username}
                onChange={e => setFilter({ ...filter, username: e.target.value })}
                placeholder={t('autenticacao.placeholderUtilizador')}
              />
            </div>
            <div className="filter-group">
              <label>{t('autenticacao.filtroTipo')}</label>
              <select
                value={filter.eventType}
                onChange={e => setFilter({ ...filter, eventType: e.target.value })}
              >
                <option value="">{t('autenticacao.todos')}</option>
                <option value="login">{t('autenticacao.tipos.login')}</option>
                <option value="logout">{t('autenticacao.tipos.logout')}</option>
                <option value="login_failed">{t('autenticacao.falhados')}</option>
              </select>
            </div>
            <div className="filter-group">
              <label>{t('autenticacao.filtroPeriodo')}</label>
              <select
                value={filter.days}
                onChange={e => setFilter({ ...filter, days: parseInt(e.target.value) })}
              >
                <option value="1">{t('autenticacao.hoje')}</option>
                <option value="7">{t('autenticacao.dias7')}</option>
                <option value="30">{t('autenticacao.dias30')}</option>
                <option value="90">{t('autenticacao.dias90')}</option>
              </select>
            </div>
          </div>
        )}

        {/* Logs Table */}
        <div className="logs-table-container">
          <table className="logs-table">
            <thead>
              <tr>
                <th>{t('autenticacao.colTipo')}</th>
                <th>{t('autenticacao.colUtilizador')}</th>
                <th>{t('autenticacao.colDataHora')}</th>
                <th>IP</th>
                <th>{t('autenticacao.colBrowser')}</th>
                <th>{t('autenticacao.colDetalhes')}</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-row">
                    {t('autenticacao.semLogs')}
                  </td>
                </tr>
              ) : (
                logs.map((log, index) => (
                  <tr key={log.id || index} className={`log-row ${log.event_type}`}>
                    <td>
                      <div className="event-type">
                        <EventIcon type={log.event_type} />
                        <span>{t('autenticacao.tipos.' + log.event_type, { defaultValue: log.event_type })}</span>
                      </div>
                    </td>
                    <td>
                      <div className="username">
                        <User size={14} />
                        {log.username}
                      </div>
                    </td>
                    <td>
                      <div className="timestamp">
                        <Clock size={14} />
                        {formatDate(log.timestamp)}
                      </div>
                    </td>
                    <td className="ip-cell">{log.ip_address || '-'}</td>
                    <td className="ua-cell">{formatUserAgent(log.user_agent)}</td>
                    <td className="details-cell">{log.details || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Last Logins (from summary) */}
        {summary?.last_logins && summary.last_logins.length > 0 && (
          <div className="last-logins-section">
            <h3>{t('autenticacao.ultimosLogins')}</h3>
            <div className="last-logins-grid">
              {summary.last_logins.map((login, index) => (
                <div key={index} className="last-login-card">
                  <User size={20} />
                  <div className="login-info">
                    <span className="login-username">{login.username}</span>
                    <span className="login-time">{formatDate(login.timestamp)}</span>
                    <span className="login-ip">{login.ip_address}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AuthLogs;
